import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, Shift, ShiftRequest, PermanentShift } from '../types';
import { api } from '../utils/api';

interface TenantPublic {
  id: string;
  name: string;
  slug: string;
  status: string;
}

interface AuthState {
  user: User | null;
  tenant: TenantPublic | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  checkAuth: () => Promise<void>;
}

interface ShiftState {
  shifts: Shift[];
  myShifts: Shift[];
  isLoading: boolean;
  error: string | null;
  fetchShifts: () => Promise<void>;
  fetchMyShifts: () => Promise<void>;
  resetShifts: () => void;
}

interface RequestState {
  requests: ShiftRequest[];
  myRequests: ShiftRequest[];
  isLoading: boolean;
  fetchRequests: () => Promise<void>;
  fetchMyRequests: () => Promise<void>;
  resetRequests: () => void;
}

interface PermanentShiftState {
  permanentShifts: PermanentShift[];
  isLoading: boolean;
  fetchPermanentShifts: () => Promise<void>;
  fetchAllPermanentShifts: () => Promise<void>;
}

interface UserManagementState {
  users: User[];
  pendingUsers: User[];
  isLoading: boolean;
  fetchUsers: () => Promise<void>;
  fetchPendingUsers: () => Promise<void>;
}

interface ToastState {
  toasts: Array<{
    id: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  }>;
  addToast: (message: string, type: 'success' | 'error' | 'warning' | 'info') => void;
  removeToast: (id: string) => void;
}

// Auth Store
export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      tenant: null,
      isAuthenticated: false,
      isLoading: true,

      login: async (username: string, password: string) => {
        const response = await api.login(username, password);
        
        if (response.success && response.data) {
          api.setToken(response.data.token);
          const tenant = (response.data as { tenant?: TenantPublic | null }).tenant ?? null;
          set({
            user: response.data.user,
            tenant,
            isAuthenticated: true,
            isLoading: false,
          });
          // Clear tenant-scoped data so we don't show another tenant's schedule/requests
          try {
            useShiftStore.getState().resetShifts?.();
            useRequestStore.getState().resetRequests?.();
          } catch (_) {}
          return { success: true };
        }
        
        return { success: false, error: response.error };
      },

      logout: () => {
        api.setToken(null);
        set({ user: null, tenant: null, isAuthenticated: false });
      },

      checkAuth: async () => {
        const token = api.getToken();
        if (!token) {
          set({ isLoading: false, isAuthenticated: false });
          return;
        }

        const response = await api.getMe();
        if (response.success && response.data) {
          const data = response.data as { user: User; tenant: TenantPublic | null };
          set({
            user: data.user,
            tenant: data.tenant ?? null,
            isAuthenticated: true,
            isLoading: false,
          });
        } else {
          api.setToken(null);
          set({ user: null, tenant: null, isAuthenticated: false, isLoading: false });
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ user: state.user, tenant: state.tenant, isAuthenticated: state.isAuthenticated }),
    }
  )
);

// Shift Store
export const useShiftStore = create<ShiftState>((set) => ({
  shifts: [],
  myShifts: [],
  isLoading: false,
  error: null,

  resetShifts: () => set({ shifts: [], myShifts: [], error: null }),

  fetchShifts: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.getShifts();
      
      if (response.success && response.data) {
        // Ensure response.data is an array
        const shiftsArray = Array.isArray(response.data) ? response.data : [];
        set({ shifts: shiftsArray, isLoading: false });
      } else {
        set({ 
          shifts: [], // Ensure shifts is always an array
          error: response.error || 'Failed to fetch shifts', 
          isLoading: false 
        });
      }
    } catch (error) {
      console.error('Error fetching shifts:', error);
      set({ 
        shifts: [], // Ensure shifts is always an array even on error
        error: 'Failed to fetch shifts', 
        isLoading: false 
      });
    }
  },

  fetchMyShifts: async () => {
    set({ isLoading: true, error: null });
    const response = await api.getMyShifts();
    
    console.log('fetchMyShifts response:', response);
    
    if (response.success && response.data) {
      // If backend returns empty array, try filtering all shifts as fallback
      if (response.data.length === 0) {
        console.log('Backend returned empty array, trying fallback: fetching all shifts and filtering...');
        
        // Get current user from auth store
        const authState = useAuthStore.getState();
        const currentUser = authState.user;
        
        if (currentUser) {
          // Fetch all shifts and filter by assigned_users
          const allShiftsResponse = await api.getShifts();
          
          if (allShiftsResponse.success && allShiftsResponse.data) {
            const myShifts = allShiftsResponse.data.filter(shift => 
              shift.assigned_users && 
              shift.assigned_users.some((user: import('../types').User) => user.id === currentUser.id)
            );
            
            console.log('Filtered my shifts from all shifts:', myShifts);
            set({ myShifts, isLoading: false, error: null });
            return;
          }
        }
      }
      
      console.log('My shifts data:', response.data);
      set({ myShifts: response.data, isLoading: false, error: null });
    } else {
      console.error('Failed to fetch my shifts:', response.error);
      set({ 
        myShifts: [], 
        isLoading: false, 
        error: response.error || 'Failed to fetch your shifts' 
      });
    }
  },
}));

// Request Store
export const useRequestStore = create<RequestState>((set) => ({
  requests: [],
  myRequests: [],
  isLoading: false,

  resetRequests: () => set({ requests: [], myRequests: [] }),

  fetchRequests: async () => {
    set({ isLoading: true });
    try {
      const response = await api.getRequests();
      
      if (response.success && response.data) {
        const requestsArray = Array.isArray(response.data) ? response.data : [];
        set({ requests: requestsArray, isLoading: false });
      } else {
        set({ 
          requests: [], // Ensure requests is always an array
          isLoading: false 
        });
      }
    } catch (error) {
      console.error('Error fetching requests:', error);
      set({ 
        requests: [], // Ensure requests is always an array even on error
        isLoading: false 
      });
    }
  },

  fetchMyRequests: async () => {
    set({ isLoading: true });
    try {
      const response = await api.getMyRequests();
      
      if (response.success && response.data) {
        const myRequestsArray = Array.isArray(response.data) ? response.data : [];
        set({ myRequests: myRequestsArray, isLoading: false });
      } else {
        set({ 
          myRequests: [], // Ensure myRequests is always an array
          isLoading: false 
        });
      }
    } catch (error) {
      console.error('Error fetching my requests:', error);
      set({ 
        myRequests: [], // Ensure myRequests is always an array even on error
        isLoading: false 
      });
    }
  },
}));

// Permanent Shift Store
export const usePermanentShiftStore = create<PermanentShiftState>((set) => ({
  permanentShifts: [],
  isLoading: false,

  fetchPermanentShifts: async () => {
    set({ isLoading: true });
    const response = await api.getPermanentShifts();
    
    if (response.success && response.data) {
      set({ permanentShifts: response.data, isLoading: false });
    } else {
      set({ isLoading: false });
    }
  },

  fetchAllPermanentShifts: async () => {
    set({ isLoading: true });
    const response = await api.getAllPermanentShifts();
    
    console.log('[PermanentShiftStore] API response:', {
      success: response.success,
      dataLength: response.data?.length || 0,
      error: response.error
    });
    
    if (response.success && response.data) {
      console.log('[PermanentShiftStore] Setting', response.data.length, 'shifts');
      // Log Wednesday 9:30 shifts specifically
      const wed930 = response.data.filter((s: any) => s.day_of_week === 'Wednesday' && s.start_time === '09:30');
      if (wed930.length > 0) {
        console.log('[PermanentShiftStore] Found Wednesday 9:30 shift:', wed930[0]);
      } else {
        console.log('[PermanentShiftStore] Wednesday 9:30 shift NOT found in response');
      }
      set({ permanentShifts: response.data, isLoading: false });
    } else {
      console.error('[PermanentShiftStore] Failed to fetch:', response.error);
      set({ isLoading: false });
    }
  },
}));

// User Management Store
export const useUserStore = create<UserManagementState>((set) => ({
  users: [],
  pendingUsers: [],
  isLoading: false,

  fetchUsers: async () => {
    set({ isLoading: true });
    try {
      const response = await api.getUsers();
      
      if (response.success && response.data) {
        // Ensure response.data is an array
        const usersArray = Array.isArray(response.data) ? response.data : [];
        set({ users: usersArray, isLoading: false });
      } else {
        set({ 
          users: [], // Ensure users is always an array
          isLoading: false 
        });
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      set({ 
        users: [], // Ensure users is always an array even on error
        isLoading: false 
      });
    }
  },

  fetchPendingUsers: async () => {
    set({ isLoading: true });
    try {
      const response = await api.getPendingUsers();
      
      if (response.success && response.data) {
        // Ensure response.data is an array
        const pendingUsersArray = Array.isArray(response.data) ? response.data : [];
        set({ pendingUsers: pendingUsersArray, isLoading: false });
      } else {
        set({ 
          pendingUsers: [], // Ensure pendingUsers is always an array
          isLoading: false 
        });
      }
    } catch (error) {
      console.error('Error fetching pending users:', error);
      set({ 
        pendingUsers: [], // Ensure pendingUsers is always an array even on error
        isLoading: false 
      });
    }
  },
}));

// Toast Store
export const useToastStore = create<ToastState>((set, get) => ({
  toasts: [],

  addToast: (message: string, type: 'success' | 'error' | 'warning' | 'info') => {
    const id = Math.random().toString(36).substring(2, 9);
    set((state) => ({
      toasts: [...state.toasts, { id, message, type }],
    }));

    // Auto-remove after 5 seconds
    setTimeout(() => {
      get().removeToast(id);
    }, 5000);
  },

  removeToast: (id: string) => {
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    }));
  },
}));
